import {
  Box,
  Button,
  ComposedChart,
  Flex,
  Loading,
  Text,
  useAppTheme,
} from 'ui'
import { useMemo } from 'react'
import { useFillRateTimeseries } from 'queries/pulse/useFillRateTimeseries'
import { usePulseContext } from '../../context/PulseProvider'
import { formatNumber, formatPercentage } from 'lib/string'
import { useFillRateTimeseriesDownloadCsv } from 'queries/pulse/useFillRateTimeseriesDownloadCsv'
import { formatTimePeriodDate } from 'pages/PulsePage/utils'

export function OverallFillRate() {
  const { colors } = useAppTheme()
  const {
    state: { filters },
  } = usePulseContext()

  const { data, isLoading } = useFillRateTimeseries(filters)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useFillRateTimeseriesDownloadCsv(filters)

  const chartData = useMemo(() => {
    return (
      data?.map((item) => {
        return {
          ...item,
          shift_date: formatTimePeriodDate(filters.timePeriod, item.shift_date),
        }
      }) ?? []
    )
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Overall Fill Rate
          </Text>
          <Text fontSize={2} color="lightText">
            Number of shifts requested paired with fill rate for the time range
            selected. Fill Rate = Number of workers staffed / Number of workers
            requested
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <ComposedChart
          data={chartData}
          dataKey="shift_date"
          height={450}
          series={[
            {
              name: 'workers_needed_final',
              color: colors.primary200,
              label: 'Workers Needed',
              chartType: 'bar',
              yAxisId: 'left',
              stackId: 'workers_needed',
            },
            {
              name: 'workers_needed_final_forecast',
              color: colors.primary100,
              label: 'Workers Needed (Upcoming)',
              chartType: 'bar',
              yAxisId: 'left',
              stackId: 'workers_needed',
            },
            {
              name: 'fill_rate',
              color: colors.info200,
              label: 'Fill Rate',
              chartType: 'line',
              yAxisId: 'right',
              strokeWidth: 2,
              withDots: true,
              withActiveDot: true,
            },
          ]}
          yRightAxisValueFormatter={(value) => formatPercentage(value, 1)}
          valueFormatter={formatNumber}
          withTooltip
          withLegend
          withPointLabels
          withXAxis
          withYAxis
        />
      )}
    </Box>
  )
}
